import PropTypes from 'prop-types';
import isString from 'lodash/isString';
import { useDropzone } from 'react-dropzone';
import { Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { useTranslation } from 'react-i18next';
import Image from '../image/Image';
import DeleteIcon from '../../assets/deletecomment.svg';

const RootStyle = styled('div')(({ theme }) => ({
  width: 144,
  height: 144,
  margin: 'auto',
  borderRadius: '50%',
  padding: theme.spacing(1),
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  position: 'relative', // required so absolute delete sits on the avatar
}));

const DropZoneStyle = styled('div')({
  zIndex: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  display: 'flex',
  overflow: 'hidden',
  borderRadius: '50%',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',
  '& > *': { width: '100%', height: '100%' },
  '&:hover': {
    cursor: 'pointer',
    '& .placeholder': {
      zIndex: 9,
    },
  },
});

const PlaceholderStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.neutral,
  transition: theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': { opacity: 0.72 },
}));

UploadAvatar.propTypes = {
  error: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.node,
  sx: PropTypes.object,
  handleDeleteProfiles: PropTypes.func,
};

export default function UploadAvatar({ error, file, helperText, sx, handleDeleteProfiles, ...other }) {
  const { t: translate } = useTranslation();
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    multiple: false,
    ...other,
  });

  return (
    <>
      <RootStyle
        sx={{
          ...((isDragReject || error) && {
            borderColor: 'error.light',
          }),
          ...sx,
        }}
      >
        {file && handleDeleteProfiles && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteProfiles();
            }}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              zIndex: 12,
              width: 28,
              height: 28,
              p: 0.5,
              bgcolor: 'background.paper',
              boxShadow: 1,
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'error.lighter',
              },
            }}
          >
            <img
              src={DeleteIcon}
              alt="Delete"
              style={{ height: 16, width: 16, display: 'block' }}
            />
          </IconButton>
        )}

        <DropZoneStyle
          {...getRootProps()}
          sx={{
            ...(isDragActive && { opacity: 0.72 }),
          }}
        >
          <input {...getInputProps()} />

          {file && <Image alt="avatar" src={isString(file) ? file : file.preview} sx={{ zIndex: 8 }} />}
          <PlaceholderStyle
            className="placeholder"
            sx={{
              ...(file && {
                opacity: 0,
                color: 'common.white',
                bgcolor: 'grey.900',
                '&:hover': { opacity: 0.72 },
              }),
              ...((isDragReject || error) && {
                bgcolor: 'error.lighter',
              }),
            }}
          >
            <AddAPhotoIcon sx={{ width: 24, height: 24, mb: 1 }} />
            <Typography variant="caption">
              {file
                ? translate('upload_avatar.update_photo', { defaultValue: 'Update photo' })
                : translate('upload_avatar.upload_photo', { defaultValue: 'Upload photo' })}
            </Typography>
          </PlaceholderStyle>
        </DropZoneStyle>
      </RootStyle>

      {helperText && helperText}
    </>
  );
}
